import { useState, useRef, useCallback } from 'react';
import { Camera, Upload, X, ImageIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { photoStorage } from '../lib/photoStorage';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function PhotoUpload({ 
  value, 
  onChange, 
  onDelete,
  categoryIcon = '📦',
  size = 'default' // 'default' | 'large'
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const processFile = useCallback(async (file) => {
    if (!file) return;

    // Validate file type
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG ou WEBP.');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Image trop volumineuse. Maximum 5 MB.');
      return;
    }

    setIsProcessing(true);
    setProgress(30);

    try {
      // Compress image
      setProgress(60);
      const compressedDataUrl = await photoStorage.compressImage(file);
      
      setProgress(90);
      onChange(compressedDataUrl);
      setProgress(100);
      
      toast.success('Photo ajoutée');
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Erreur lors du traitement de l\'image');
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  }, [onChange]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    // Reset input to allow selecting same file again
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDelete = () => {
    onChange(null);
    if (onDelete) onDelete();
    toast.success('Photo supprimée');
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const openCamera = () => {
    cameraInputRef.current?.click();
  };

  const sizeClasses = size === 'large' 
    ? 'w-full h-40 min-h-[160px]' 
    : 'w-32 h-32 min-w-[128px] min-h-[128px]';

  return (
    <div className="space-y-2">
      <div
        className={`relative ${sizeClasses} rounded-xl border-2 border-dashed transition-all ${
          isDragging 
            ? 'border-primary bg-primary/10' 
            : value 
              ? 'border-transparent' 
              : 'border-border hover:border-primary/50 hover:bg-secondary/50'
        } ${isProcessing ? 'pointer-events-none' : 'cursor-pointer'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!value && !isProcessing ? openFilePicker : undefined}
        data-testid="photo-upload-zone"
      >
        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="photo-file-input"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="photo-camera-input"
        />

        {/* Processing state */}
        {isProcessing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 rounded-xl z-10">
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
            <div className="w-24 h-2 bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Traitement...</p>
          </div>
        )}

        {/* Photo preview */}
        {value && !isProcessing && (
          <>
            <img 
              src={value} 
              alt="Aperçu" 
              className="w-full h-full object-cover rounded-xl"
              data-testid="photo-preview"
            />
            {/* Delete button */}
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute -top-2 -right-2 h-7 w-7 rounded-full shadow-lg"
              onClick={(e) => { e.stopPropagation(); handleDelete(); }}
              data-testid="delete-photo-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          </>
        )}

        {/* Empty state */}
        {!value && !isProcessing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
            {isDragging ? (
              <>
                <Upload className="w-8 h-8 text-primary mb-2" />
                <p className="text-sm text-primary font-medium">Déposez ici</p>
              </>
            ) : (
              <>
                <div className="text-4xl mb-2">{categoryIcon}</div>
                <p className="text-xs text-center px-2">
                  Cliquez ou glissez une photo
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Action buttons when no photo */}
      {!value && !isProcessing && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openFilePicker}
            className="flex-1"
            data-testid="choose-photo-btn"
          >
            <ImageIcon className="w-4 h-4 mr-2" />
            Galerie
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openCamera}
            className="flex-1"
            data-testid="take-photo-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            Caméra
          </Button>
        </div>
      )}

      {/* Change photo button when photo exists */}
      {value && !isProcessing && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openFilePicker}
            className="flex-1"
            data-testid="change-photo-btn"
          >
            <ImageIcon className="w-4 h-4 mr-2" />
            Changer
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openCamera}
            className="flex-1"
            data-testid="retake-photo-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            Reprendre
          </Button>
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center">
        JPG, PNG, WEBP • Max 5 MB
      </p>
    </div>
  );
}
